import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  X,
  Building2,
  MapPin,
  Wrench,
  Droplet,
  Clock,
  Edit3,
  Save,
  Loader2,
  FileText,
  History,
  ExternalLink,
  CalendarDays,
  AlertCircle
} from 'lucide-react';
import { franklinAPI } from '@shared/apiClient';
import { formatDate } from '@shared/utils';
import toast from 'react-hot-toast';
import CopyTextButton from './CopyTextButton';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const EQUIPMENT_FIELDS = [
  { key: 'generatorType', label: 'Type', group: 'generator' },
  { key: 'generatorModel', label: 'Model', group: 'generator' },
  { key: 'generatorSerialNumber', label: 'Serial Number', group: 'generator' },
  { key: 'generatorSize', label: 'Size', group: 'generator' },
  {
    key: 'generatorCoolingType',
    label: 'Cooling Type',
    group: 'generator',
    type: 'select',
    options: [
      { value: '', label: 'Unknown' },
      { value: 'air', label: 'Air-cooled' },
      { value: 'water', label: 'Water-cooled' }
    ]
  },
  { key: 'generatorExerciseTime', label: 'Exercise Time', group: 'generator' },
  { key: 'airFilter', label: 'Air Filter', group: 'filters' },
  { key: 'fuelFilter1', label: 'Fuel Filter 1', group: 'filters' },
  { key: 'fuelFilter2', label: 'Fuel Filter 2', group: 'filters' },
  { key: 'oilFilter1', label: 'Oil Filter 1', group: 'filters' },
  { key: 'oilFilter2', label: 'Oil Filter 2', group: 'filters' },
  { key: 'fuelAdditive', label: 'Fuel Additive', group: 'fluids' },
  { key: 'fuelType', label: 'Fuel Type', group: 'fluids' },
  { key: 'oilType', label: 'Oil Type', group: 'fluids' },
  { key: 'oilAmount', label: 'Oil Amount', group: 'fluids' },
  { key: 'batterySize', label: 'Size', group: 'battery' },
  { key: 'batteryDateCode', label: 'Date Code', group: 'battery' },
  { key: 'batteryDateChanged', label: 'Date Changed', group: 'battery', type: 'date' },
];

const DETAILS_FIELDS = [
  { key: 'locationName', label: 'Location Name', group: 'location' },
  { key: 'address', label: 'Address', group: 'location' },
  { key: 'city', label: 'City', group: 'location' },
  { key: 'state', label: 'State', group: 'location' },
  { key: 'zipCode', label: 'Zip Code', group: 'location' },
  { key: 'county', label: 'County', group: 'location' },
  { key: 'district', label: 'District', group: 'location' },
  { key: 'latitude', label: 'Latitude', group: 'location', type: 'number' },
  { key: 'longitude', label: 'Longitude', group: 'location', type: 'number' },
  { key: 'backupPower', label: 'Backup Power', group: 'siteInfo' },
  { key: 'tankSize', label: 'Tank Size', group: 'siteInfo' },
  { key: 'monthYearPlaced', label: 'Placed', group: 'siteInfo' },
  { key: 'herringGasRef', label: 'Herring Gas Ref', group: 'siteInfo' },
];

const StatusBadge = ({ status }) => {
  const configs = {
    assigned: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-purple-100 text-purple-800',
    completed: 'bg-green-100 text-green-800',
    return: 'bg-orange-100 text-orange-800',
    cancelled: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${configs[status] || 'bg-gray-100 text-gray-800'}`}>
      {status?.replace('_', ' ') || 'N/A'}
    </span>
  );
};

export default function FranklinSiteDetailsModal({ siteId, isOpen, onClose, onViewServiceCall }) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('details');
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [formData, setFormData] = useState({});
  const [detailsFormData, setDetailsFormData] = useState({});
  const [serviceFormData, setServiceFormData] = useState({
    serviceCycleMonths: [],
    oilChangeMonth: '',
    notes: ''
  });

  const { data: siteData, isLoading } = useQuery(
    ['franklinSite', siteId],
    () => franklinAPI.getSite(siteId),
    {
      enabled: isOpen && !!siteId,
      refetchOnWindowFocus: false
    }
  );

  const site = siteData?.data?.site;

  useEffect(() => {
    if (isOpen && site) {
      setActiveTab('details');
      setIsEditing(false);
      setIsEditingDetails(false);

      const eqData = {};
      EQUIPMENT_FIELDS.forEach(({ key, type }) => {
        if (type === 'date') {
          eqData[key] = site[key] ? new Date(site[key]).toISOString().split('T')[0] : '';
        } else {
          eqData[key] = site[key] || '';
        }
      });
      setFormData(eqData);

      const dtData = {};
      DETAILS_FIELDS.forEach(({ key }) => {
        dtData[key] = site[key] != null ? String(site[key]) : '';
      });
      setDetailsFormData(dtData);

      setServiceFormData({
        serviceCycleMonths: site.serviceCycleMonths || [],
        oilChangeMonth: site.oilChangeMonth || '',
        notes: site.notes || ''
      });
    }
  }, [isOpen, site]);

  const updateMutation = useMutation(
    (payload) => franklinAPI.updateSite(siteId, payload),
    {
      onSuccess: () => {
        toast.success('Franklin site updated!');
        queryClient.invalidateQueries(['franklinSite', siteId]);
        queryClient.invalidateQueries('franklinSites');
        setIsEditing(false);
        setIsEditingDetails(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update site');
      }
    }
  );

  if (!isOpen) return null;

  if (isLoading || !site) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
          <div className="relative bg-white rounded-lg p-8 shadow-xl">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600 mx-auto" />
            <p className="mt-2 text-sm text-gray-600">Loading site details...</p>
          </div>
        </div>
      </div>
    );
  }

  const serviceCalls = site.serviceCalls || [];
  const serviceCutoff = new Date();
  serviceCutoff.setMonth(serviceCutoff.getMonth() - (site.serviceCycleMonths || 6));
  const needsService = !site.lastServiceDate || new Date(site.lastServiceDate) < serviceCutoff;

  const handleSaveEquipment = async () => {
    const payload = {};
    EQUIPMENT_FIELDS.forEach(({ key }) => {
      payload[key] = formData[key]?.trim?.() || null;
    });
    await updateMutation.mutateAsync(payload);
  };

  const handleSaveServiceConfig = async () => {
    await updateMutation.mutateAsync({
      serviceCycleMonths: serviceFormData.serviceCycleMonths.length > 0
        ? serviceFormData.serviceCycleMonths
        : null,
      oilChangeMonth: serviceFormData.oilChangeMonth || null,
      notes: serviceFormData.notes || null
    });
  };

  const handleCancelEdit = () => {
    const eqData = {};
    EQUIPMENT_FIELDS.forEach(({ key, type }) => {
      if (type === 'date') {
        eqData[key] = site[key] ? new Date(site[key]).toISOString().split('T')[0] : '';
      } else {
        eqData[key] = site[key] || '';
      }
    });
    setFormData(eqData);
    setIsEditing(false);
  };

  const handleSaveDetails = async () => {
    const payload = {};
    DETAILS_FIELDS.forEach(({ key, type }) => {
      const val = detailsFormData[key]?.trim?.() || null;
      if (type === 'number' && val) {
        payload[key] = parseFloat(val);
      } else {
        payload[key] = val;
      }
    });
    await updateMutation.mutateAsync(payload);
  };

  const handleCancelDetailsEdit = () => {
    const dtData = {};
    DETAILS_FIELDS.forEach(({ key }) => {
      dtData[key] = site[key] != null ? String(site[key]) : '';
    });
    setDetailsFormData(dtData);
    setIsEditingDetails(false);
  };

  const toggleServiceMonth = (month) => {
    setServiceFormData(prev => {
      const current = prev.serviceCycleMonths || [];
      if (current.includes(month)) {
        return { ...prev, serviceCycleMonths: current.filter(m => m !== month) };
      }
      if (current.length >= 2) return prev;
      return { ...prev, serviceCycleMonths: [...current, month].sort((a, b) => a - b) };
    });
  };

  const dash = <span className="text-gray-400">—</span>;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-emerald-50 px-4 py-5 sm:px-6 border-b border-emerald-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Building2 className="h-6 w-6 text-emerald-600" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {site.locationName || site.siteId}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {site.company} • {site.siteId}
                    {needsService && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-600 text-white">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Needs Service
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {[
                { id: 'details', label: 'Details' },
                { id: 'equipment', label: 'Equipment' },
                { id: 'history', label: `Service History (${serviceCalls.length})` }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-emerald-500 text-emerald-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="px-4 py-5 sm:p-6 max-h-[70vh] overflow-y-auto">
            {activeTab === 'details' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Location & Site Info */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-900">Site Details</span>
                    <button
                      type="button"
                      onClick={() => isEditingDetails ? handleCancelDetailsEdit() : setIsEditingDetails(true)}
                      className="text-xs text-emerald-600 hover:text-emerald-800 flex items-center gap-1"
                    >
                      <Edit3 className="h-3 w-3" />
                      {isEditingDetails ? 'Cancel' : 'Edit'}
                    </button>
                  </div>

                  {/* Location Name */}
                  <div>
                    <dt className="text-xs font-medium text-gray-500">Location Name</dt>
                    {isEditingDetails ? (
                      <input
                        type="text"
                        value={detailsFormData.locationName || ''}
                        onChange={(e) => setDetailsFormData({ ...detailsFormData, locationName: e.target.value })}
                        className="w-full mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    ) : (
                      <dd className="mt-0.5 text-sm text-gray-900">{site.locationName || dash}</dd>
                    )}
                  </div>

                  {/* Location Info */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <MapPin className="h-4 w-4 mr-2 text-emerald-600" />
                      Location
                    </h4>
                    <dl className="space-y-2">
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Address</dt>
                        {isEditingDetails ? (
                          <input
                            type="text"
                            value={detailsFormData.address || ''}
                            onChange={(e) => setDetailsFormData({ ...detailsFormData, address: e.target.value })}
                            className="w-full mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                          />
                        ) : (
                          <dd className="mt-0.5 text-sm text-gray-900">{site.address || dash}</dd>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <dt className="text-xs font-medium text-gray-500">City</dt>
                          {isEditingDetails ? (
                            <input
                              type="text"
                              value={detailsFormData.city || ''}
                              onChange={(e) => setDetailsFormData({ ...detailsFormData, city: e.target.value })}
                              className="w-full mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                            />
                          ) : (
                            <dd className="mt-0.5 text-sm text-gray-900">{site.city || dash}</dd>
                          )}
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-gray-500">State</dt>
                          {isEditingDetails ? (
                            <input
                              type="text"
                              value={detailsFormData.state || ''}
                              onChange={(e) => setDetailsFormData({ ...detailsFormData, state: e.target.value })}
                              className="w-full mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                              maxLength={2}
                            />
                          ) : (
                            <dd className="mt-0.5 text-sm text-gray-900">{site.state || dash}</dd>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <dt className="text-xs font-medium text-gray-500">Zip Code</dt>
                          {isEditingDetails ? (
                            <input
                              type="text"
                              value={detailsFormData.zipCode || ''}
                              onChange={(e) => setDetailsFormData({ ...detailsFormData, zipCode: e.target.value })}
                              className="w-full mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                              maxLength={10}
                            />
                          ) : (
                            <dd className="mt-0.5 text-sm text-gray-900">{site.zipCode || dash}</dd>
                          )}
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-gray-500">County</dt>
                          {isEditingDetails ? (
                            <input
                              type="text"
                              value={detailsFormData.county || ''}
                              onChange={(e) => setDetailsFormData({ ...detailsFormData, county: e.target.value })}
                              className="w-full mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                            />
                          ) : (
                            <dd className="mt-0.5 text-sm text-gray-900">{site.county || dash}</dd>
                          )}
                        </div>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500">District</dt>
                        {isEditingDetails ? (
                          <input
                            type="text"
                            value={detailsFormData.district || ''}
                            onChange={(e) => setDetailsFormData({ ...detailsFormData, district: e.target.value })}
                            className="w-full mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                          />
                        ) : (
                          <dd className="mt-0.5 text-sm text-gray-900">{site.district || dash}</dd>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <dt className="text-xs font-medium text-gray-500">Latitude</dt>
                          {isEditingDetails ? (
                            <input
                              type="text"
                              value={detailsFormData.latitude || ''}
                              onChange={(e) => setDetailsFormData({ ...detailsFormData, latitude: e.target.value })}
                              className="w-full mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                            />
                          ) : (
                            <dd className="mt-0.5 text-sm text-gray-900">
                              {site.latitude || dash}
                            </dd>
                          )}
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-gray-500">Longitude</dt>
                          {isEditingDetails ? (
                            <input
                              type="text"
                              value={detailsFormData.longitude || ''}
                              onChange={(e) => setDetailsFormData({ ...detailsFormData, longitude: e.target.value })}
                              className="w-full mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                            />
                          ) : (
                            <dd className="mt-0.5 text-sm text-gray-900">
                              {site.longitude || dash}
                            </dd>
                          )}
                        </div>
                      </div>
                      {!isEditingDetails && (site.latitude && site.longitude) && (
                        <div className="flex justify-end">
                          <a
                            href={`https://www.google.com/maps?q=${site.latitude},${site.longitude}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-emerald-600 hover:text-emerald-800 flex items-center gap-1"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                            View on Map
                          </a>
                        </div>
                      )}
                    </dl>
                  </div>

                  {/* Site-Specific Info */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <Building2 className="h-4 w-4 mr-2 text-emerald-600" />
                      Site Info
                    </h4>
                    <dl className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <dt className="text-xs font-medium text-gray-500">Backup Power</dt>
                          {isEditingDetails ? (
                            <input
                              type="text"
                              value={detailsFormData.backupPower || ''}
                              onChange={(e) => setDetailsFormData({ ...detailsFormData, backupPower: e.target.value })}
                              className="w-full mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                            />
                          ) : (
                            <dd className="mt-0.5 text-sm text-gray-900">{site.backupPower || dash}</dd>
                          )}
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-gray-500">Tank Size</dt>
                          {isEditingDetails ? (
                            <input
                              type="text"
                              value={detailsFormData.tankSize || ''}
                              onChange={(e) => setDetailsFormData({ ...detailsFormData, tankSize: e.target.value })}
                              className="w-full mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                            />
                          ) : (
                            <dd className="mt-0.5 text-sm text-gray-900">{site.tankSize || dash}</dd>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <dt className="text-xs font-medium text-gray-500">Placed</dt>
                          {isEditingDetails ? (
                            <input
                              type="text"
                              value={detailsFormData.monthYearPlaced || ''}
                              onChange={(e) => setDetailsFormData({ ...detailsFormData, monthYearPlaced: e.target.value })}
                              className="w-full mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                            />
                          ) : (
                            <dd className="mt-0.5 text-sm text-gray-900">{site.monthYearPlaced || dash}</dd>
                          )}
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-gray-500">Herring Gas Ref</dt>
                          {isEditingDetails ? (
                            <input
                              type="text"
                              value={detailsFormData.herringGasRef || ''}
                              onChange={(e) => setDetailsFormData({ ...detailsFormData, herringGasRef: e.target.value })}
                              className="w-full mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                            />
                          ) : (
                            <dd className="mt-0.5 text-sm text-gray-900">{site.herringGasRef || dash}</dd>
                          )}
                        </div>
                      </div>
                    </dl>
                  </div>

                  {isEditingDetails && (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={handleSaveDetails}
                        disabled={updateMutation.isLoading}
                        className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {updateMutation.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Save Details
                      </button>
                    </div>
                  )}

                  {/* System Info */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">System Info</h4>
                    <dl className="space-y-2">
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Status</dt>
                        <dd className="mt-0.5">
                          {site.isActive !== false ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Inactive</span>
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-gray-500">Last Service</dt>
                        <dd className="mt-0.5 text-sm text-gray-900">
                          {site.lastServiceDate ? formatDate(site.lastServiceDate) : 'Never'}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>

                {/* Service Configuration */}
                <div className="space-y-4">
                  <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <CalendarDays className="h-4 w-4 mr-2 text-emerald-600" />
                      Service Configuration
                    </h4>

                    <div className="mb-4">
                      <dt className="text-xs font-medium text-gray-600 mb-2">Service Cycle Months (select up to 2)</dt>
                      <div className="grid grid-cols-4 gap-1">
                        {MONTH_NAMES.map((name, idx) => {
                          const month = idx + 1;
                          const selected = (serviceFormData.serviceCycleMonths || []).includes(month);
                          return (
                            <button
                              key={month}
                              type="button"
                              onClick={() => toggleServiceMonth(month)}
                              className={`px-2 py-1.5 text-xs rounded font-medium transition-colors ${
                                selected
                                  ? 'bg-emerald-600 text-white'
                                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {name.substring(0, 3)}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="mb-4">
                      <dt className="text-xs font-medium text-gray-600 mb-1">Oil Change Month</dt>
                      <select
                        value={serviceFormData.oilChangeMonth || ''}
                        onChange={(e) => setServiceFormData(prev => ({
                          ...prev,
                          oilChangeMonth: e.target.value ? parseInt(e.target.value) : ''
                        }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-emerald-500 focus:border-emerald-500"
                      >
                        <option value="">Not set</option>
                        {MONTH_NAMES.map((name, idx) => (
                          <option key={idx + 1} value={idx + 1}>{name}</option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        The month designated for oil change; other service months are inspection only.
                      </p>
                    </div>

                    <div className="mb-4">
                      <dt className="text-xs font-medium text-gray-600 mb-1">Notes</dt>
                      <textarea
                        value={serviceFormData.notes || ''}
                        onChange={(e) => setServiceFormData(prev => ({ ...prev, notes: e.target.value }))}
                        rows={3}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={handleSaveServiceConfig}
                        disabled={updateMutation.isLoading}
                        className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {updateMutation.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Save Configuration
                      </button>
                    </div>
                  </div>

                  {/* Current Config Summary */}
                  {(site.serviceCycleMonths?.length > 0 || site.oilChangeMonth) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Current Schedule</h4>
                      {site.serviceCycleMonths?.length > 0 && (
                        <p className="text-sm text-gray-700">
                          <span className="font-medium">Service months:</span>{' '}
                          {site.serviceCycleMonths.map(m => MONTH_NAMES[m - 1]).join(', ')}
                        </p>
                      )}
                      {site.oilChangeMonth && (
                        <p className="text-sm text-gray-700">
                          <span className="font-medium">Oil change month:</span>{' '}
                          {MONTH_NAMES[site.oilChangeMonth - 1]}
                        </p>
                      )}
                    </div>
                  )}

                  {site.notes && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                        <FileText className="h-4 w-4 mr-2 text-gray-600" />
                        Notes
                      </h4>
                      <p className="text-xs text-gray-700 whitespace-pre-wrap">{site.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'equipment' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Generator */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-900">Equipment Details</span>
                    <button
                      type="button"
                      onClick={() => isEditing ? handleCancelEdit() : setIsEditing(true)}
                      className="text-xs text-emerald-600 hover:text-emerald-800 flex items-center gap-1"
                    >
                      <Edit3 className="h-3 w-3" />
                      {isEditing ? 'Cancel' : 'Edit'}
                    </button>
                  </div>

                  <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <Wrench className="h-4 w-4 mr-2 text-blue-600" />
                      Generator Details
                    </h4>
                    <dl className="space-y-2">
                      {EQUIPMENT_FIELDS.filter(f => f.group === 'generator').map(({ key, label, type, options }) => (
                        <div key={key}>
                          <dt className="text-xs font-medium text-gray-600">{label}</dt>
                          {isEditing ? (
                            type === 'select' ? (
                              <select
                                value={formData[key] || ''}
                                onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                                className="w-full mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                              >
                                {options.map((opt) => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type={type === 'date' ? 'date' : 'text'}
                                value={formData[key] || ''}
                                onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                                className="w-full mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                              />
                            )
                          ) : (
                            <dd className="mt-0.5 text-sm text-gray-900">
                              {key === 'generatorSerialNumber' ? (
                                <span className="font-mono">{site[key] || dash}</span>
                              ) : type === 'select' ? (
                                options.find((opt) => opt.value === site[key])?.label || dash
                              ) : (
                                site[key] || dash
                              )}
                            </dd>
                          )}
                        </div>
                      ))}
                    </dl>
                  </div>

                  {/* Battery */}
                  <div className="bg-green-50 border-2 border-green-200 rounded-lg p-3">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <Wrench className="h-4 w-4 mr-2 text-green-600" />
                      Battery Details
                    </h4>
                    <dl className="space-y-2">
                      {EQUIPMENT_FIELDS.filter(f => f.group === 'battery').map(({ key, label, type }) => (
                        <div key={key}>
                          <dt className="text-xs font-medium text-gray-600">{label}</dt>
                          {isEditing ? (
                            <input
                              type={type === 'date' ? 'date' : 'text'}
                              value={formData[key] || ''}
                              onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                              className="w-full mt-0.5 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                            />
                          ) : (
                            <dd className="mt-0.5 text-sm text-gray-900">
                              {type === 'date' && site[key] ? formatDate(site[key]) : site[key] || dash}
                            </dd>
                          )}
                        </div>
                      ))}
                    </dl>
                  </div>

                  {isEditing && (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={handleSaveEquipment}
                        disabled={updateMutation.isLoading}
                        className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {updateMutation.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Save Equipment Info
                      </button>
                    </div>
                  )}
                </div>

                {/* Filters & Fluids */}
                <div className="lg:col-span-2">
                  <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-3">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <Droplet className="h-4 w-4 mr-2 text-amber-600" />
                      Filters & Fluids
                    </h4>
                    <dl className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        {EQUIPMENT_FIELDS.filter(f => f.group === 'filters').map(({ key, label }) => (
                          <div key={key}>
                            <dt className="text-xs font-medium text-gray-600">{label}</dt>
                            {isEditing ? (
                              <input
                                type="text"
                                value={formData[key] || ''}
                                onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                                className="w-full mt-0.5 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                              />
                            ) : (
                              <dd className="mt-0.5 text-xs text-gray-900">{site[key] || dash}</dd>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="pt-2 border-t border-amber-300">
                        <div className="grid grid-cols-2 gap-2">
                          {EQUIPMENT_FIELDS.filter(f => f.group === 'fluids').map(({ key, label }) => (
                            <div key={key}>
                              <dt className="text-xs font-medium text-gray-600">{label}</dt>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={formData[key] || ''}
                                  onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                                  className="w-full mt-0.5 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                                />
                              ) : (
                                <dd className="mt-0.5 text-xs text-gray-900">{site[key] || dash}</dd>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </dl>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'history' && (
              <div>
                {serviceCalls.length === 0 ? (
                  <div className="text-center py-12">
                    <Clock className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-4 text-lg font-medium text-gray-900">No Service History</h3>
                    <p className="mt-2 text-sm text-gray-500">
                      This site doesn't have any service calls yet.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {serviceCalls.map((sc) => {
                      const visits = Array.isArray(sc.visits) ? sc.visits : [];
                      return (
                        <div key={sc.id} className="border border-gray-200 rounded-lg p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <StatusBadge status={sc.status} />
                              {sc.isFinalized && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                                  Finalized
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {sc.completedDate && (
                                <span className="text-xs text-gray-500">
                                  Completed: {formatDate(sc.completedDate)}
                                </span>
                              )}
                              {!sc.completedDate && sc.createdAt && (
                                <span className="text-xs text-gray-500">
                                  Created: {formatDate(sc.createdAt)}
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => onViewServiceCall?.(sc)}
                                className="text-xs text-emerald-600 hover:text-emerald-800 font-medium"
                              >
                                View Service Call
                              </button>
                            </div>
                          </div>

                          {sc.technician && (
                            <div className="text-xs text-gray-600">
                              <span className="font-medium">Technician:</span> {sc.technician.firstName} {sc.technician.lastName}
                            </div>
                          )}

                          {sc.description && (
                            <div className="text-xs text-gray-600">
                              <span className="font-medium">Description:</span> {sc.description}
                            </div>
                          )}

                          {sc.workPerformed && (
                            <div>
                              <div className="mb-1 flex items-center justify-between gap-2">
                                <span className="text-xs font-medium text-gray-700">Work Performed</span>
                                <CopyTextButton text={sc.workPerformed} size="xs" />
                              </div>
                              <div className="bg-gray-50 rounded p-2 text-sm text-gray-700 whitespace-pre-wrap">
                                {sc.workPerformed}
                              </div>
                            </div>
                          )}

                          {visits.length > 0 && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <History className="h-4 w-4 text-yellow-700" />
                                <h4 className="text-sm font-semibold text-gray-900">
                                  Visit History ({visits.length} visit{visits.length > 1 ? 's' : ''})
                                </h4>
                              </div>
                              <div className="space-y-2">
                                {visits.map((visit) => (
                                  <div key={visit.id} className="bg-white rounded-md p-2 border border-yellow-100 text-sm">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="font-medium text-gray-900">Visit #{visit.visitNumber}</span>
                                      <span className="text-xs text-gray-600">{formatDate(visit.visitDate)}</span>
                                    </div>
                                    {visit.workPerformed && (
                                      <div>
                                        <div className="mb-0.5 flex items-center justify-between gap-1">
                                          <span className="text-xs font-medium text-gray-600">Work Performed:</span>
                                          <CopyTextButton text={visit.workPerformed} size="xs" />
                                        </div>
                                        <p className="text-gray-700 text-xs whitespace-pre-wrap">{visit.workPerformed}</p>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
